import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { API_URL } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const fallbackImage =
  "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=1200&auto=format&fit=crop&q=80";

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const getImageSrc = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") return fallbackImage;
  const clean = imageUrl.trim();
  if (!clean) return fallbackImage;
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

const ProjectDetails = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/projects/${slug}`);
      if (!res.ok) throw new Error("Project not found");
      const data = await res.json();
      setProject(data);

      const relRes = await fetch(
        `${API_URL}/projects?is_active=true&focus_area=${encodeURIComponent(data.focus_area || "")}`
      );
      if (relRes.ok) {
        const relData = await relRes.json();
        const list = Array.isArray(relData) ? relData : [];
        setRelated(list.filter((p) => p.id !== data.id).slice(0, 6));
      } else {
        setRelated([]);
      }
    } catch (err) {
      setError(err.message || "Unable to load project");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) loadData();
  }, [slug]);

  const title = useMemo(() => project?.title || "Project", [project]);
  const gallery = useMemo(
    () => (Array.isArray(project?.gallery) ? project.gallery : []),
    [project]
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="h-10 w-40 bg-stone-200 rounded mb-6 animate-pulse" />
        <div className="h-80 bg-stone-200 rounded-2xl animate-pulse mb-6" />
        <div className="h-8 w-2/3 bg-stone-200 rounded mb-4 animate-pulse" />
        <div className="h-5 w-full bg-stone-200 rounded mb-2 animate-pulse" />
        <div className="h-5 w-5/6 bg-stone-200 rounded animate-pulse" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-20">
        <div className="rounded-2xl border border-red-200 bg-red-50 text-red-700 px-5 py-4">
          {error || "Project not found"}
        </div>
        <div className="mt-6">
          <Link to="/projects" className="text-amber-700 hover:text-amber-800 font-medium">
            ← Back to projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative">
        <div className="h-[300px] md:h-[440px] overflow-hidden">
          <img
            src={getImageSrc(project.image_url)}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/10" />
        </div>

        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-8 md:pb-12">
            <Link
              to="/projects"
              className="inline-flex items-center text-white/95 hover:text-white mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Projects
            </Link>

            <span className="inline-flex text-xs md:text-sm px-3 py-1 rounded-full border border-amber-200 bg-amber-100 text-amber-800 mb-4">
              {project.focus_area || "General"}
            </span>

            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight max-w-4xl">
              {title}
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid lg:grid-cols-[1fr,330px] gap-10">
          <article>
            <p className="text-lg md:text-xl text-stone-600 leading-relaxed mb-8 border-l-4 border-amber-400 pl-4">
              {project.excerpt || project.description}
            </p>

            <div
              className="
                prose prose-stone max-w-none
                prose-headings:font-heading prose-headings:text-stone-900
                prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl
                prose-p:text-stone-700 prose-p:leading-8 prose-p:mb-6
                prose-li:leading-8
                prose-a:text-amber-700 hover:prose-a:text-amber-800
                prose-img:rounded-2xl prose-img:shadow-md
                prose-blockquote:border-amber-300 prose-blockquote:text-stone-700
              "
              dangerouslySetInnerHTML={{
                __html:
                  project.content ||
                  `<p>${(project.description || "").replace(/\n/g, "<br/>")}</p>`,
              }}
            />

            {gallery.length > 0 && (
              <div className="mt-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 mb-5">
                  Project Gallery
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {gallery.map((g, i) => (
                    <a key={i} href={getImageSrc(g)} target="_blank" rel="noreferrer">
                      <img
                        src={getImageSrc(g)}
                        alt={`${title} gallery ${i + 1}`}
                        className="w-full h-44 md:h-52 object-cover rounded-xl border border-stone-200 hover:shadow-md transition"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild className="bg-amber-700 hover:bg-amber-800 rounded-full px-7 py-4">
                <Link to="/get-involved#donate">
                  Support This Project <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-7 py-4">
                <Link to="/get-involved#volunteer">Volunteer</Link>
              </Button>
            </div>
          </article>

          <aside className="space-y-5">
            <Card className="rounded-2xl border-stone-200 shadow-sm">
              <CardContent className="p-5">
                <h3 className="font-semibold text-stone-900 mb-3">Project Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-stone-500">Category</span>
                    <span className="text-stone-800 font-medium text-right">{project.focus_area || "General"}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-stone-500">Status</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${project.is_active ? "bg-green-100 text-green-700" : "bg-stone-200 text-stone-700"}`}>
                      {project.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-200 shadow-sm">
              <CardContent className="p-5">
                <h3 className="font-semibold text-stone-900 mb-4">Related Projects</h3>
                <div className="space-y-4">
                  {related.length === 0 ? (
                    <p className="text-sm text-stone-500">No related projects yet.</p>
                  ) : (
                    related.map((item) => (
                      <Link
                        key={item.id}
                        to={`/projects/${item.slug || item.id}`}
                        className="block group"
                      >
                        <div className="flex gap-3">
                          <img
                            src={getImageSrc(item.image_url)}
                            alt={item.title}
                            className="w-16 h-16 rounded-lg object-cover border border-stone-200"
                          />
                          <div>
                            <p className="text-sm font-medium text-stone-800 group-hover:text-amber-700 line-clamp-2">
                              {item.title}
                            </p>
                            <p className="text-xs text-stone-500 mt-1">{item.focus_area}</p>
                          </div>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetails;
